import { useQueryClient } from "@tanstack/react-query";
import {
  Compass,
  Download,
  FolderKanban,
  LogOut,
  MessagesSquare,
  ChevronDown,
  FolderGit2,
  Pin,
  PinOff,
  Puzzle,
  Settings,
  Shield,
  User,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { clearLocalCollabData } from "@/collab/session";
import { desktop } from "@/desktop/bridge";
import { FULL_EDITION, hasFeature, type DesktopFeature } from "@/desktop/edition";
import { useOrbitWatcher } from "@/pages/discover/watch";
import { post } from "@/lib/api";
import { useChatSocket, useUnreadTitle } from "@/chat/socket";
import { useChatUnread, useConversations, useGroups, useMe, useProjects } from "@/lib/queries";
import { useHome, usePins } from "@/lib/home";
import { GroupAvatar, PendingInvites, ProfileStatus, SidebarFooterLinks } from "./sidebar-parts";
import { cn } from "@/lib/utils";
import { Notifications } from "./notifications";
import { Avatar } from "./ui/controls";
import { Logo } from "./ui/logo";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tip } from "./ui/overlay";
import { DOWNLOAD_URL } from "@/lib/landing";

const NAV: Array<{ to: string; label: string; icon: typeof Compass; feature?: DesktopFeature }> = [
  { to: "/projects", label: "Projetos", icon: FolderKanban },
  { to: "/chat", label: "Mensagens", icon: MessagesSquare },
  { to: "/discover", label: "Descobrir devs", icon: Compass, feature: "network" },
];

/** Na web (topo), Grupos e Plugins seguem no menu; no app, Grupos é a seção e Plugins o rodapé. */
const WEB_NAV: typeof NAV = [
  ...NAV,
  { to: "/groups", label: "Grupos", icon: Users, feature: "groups" },
  { to: "/plugins", label: "Plugins", icon: Puzzle, feature: "plugins" },
];

/** Web (versão resumida): projetos na nuvem, conversas e grupos. O app desktop mostra tudo. */
const VISIBLE_NAV = WEB_NAV.filter((n) => !n.feature || hasFeature(n.feature));

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await post("/auth/logout").catch(() => {});
    // Privacidade: o cache local de código não fica na máquina depois de sair.
    await clearLocalCollabData().catch(() => {});
    qc.clear();
    navigate("/login");
  };
}

const isActivePath = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(`${to}/`) || (to === "/groups" && pathname.startsWith("/g/"));

function CountPill({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1.5 text-[10.5px] font-semibold text-accent-fg">
      {n > 99 ? "99+" : n}
    </span>
  );
}

function SidebarTitle({
  children,
  action,
  to,
  open,
  onToggle,
}: {
  children: ReactNode;
  action?: ReactNode;
  /** O título leva à página completa (ex.: Grupos). */
  to?: string;
  /** Seção recolhível. */
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <p className="flex items-center gap-1 px-2.5 pb-1 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? "Recolher" : "Expandir"}
          className="-ml-1 rounded p-0.5 hover:bg-bg-muted hover:text-fg"
        >
          <ChevronDown className={cn("h-3 w-3 transition-transform", !open && "-rotate-90")} />
        </button>
      ) : null}
      {to ? (
        <Link to={to} className="hover:text-fg hover:underline">
          {children}
        </Link>
      ) : (
        children
      )}
      <span className="ml-auto flex items-center">{action}</span>
    </p>
  );
}

const SECTION_KEY = "kompil.sidebar.sections";
function readSections(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(SECTION_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

/**
 * App desktop, páginas fora de um projeto: a navegação, os convites pendentes, os fixados (projetos e grupos), quem está online agora e os grupos (com cor,
 * não lidas e quem está online, como servidores no Discord). No rodapé, você com o seu status,
 * Plugins e Ajustes.
 */
function DesktopShell({ children, unreadChat }: { children: ReactNode; unreadChat: number }) {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const account = Boolean(me) && !me?.user.isGuest;
  const { data: projects } = useProjects(Boolean(me));
  const { data: home } = useHome(account);
  const { data: groups } = useGroups(account);
  const { data: conversations } = useConversations(account);
  const pins = usePins((s) => s.ids);
  const togglePin = usePins((s) => s.toggle);
  const [sections, setSections] = useState(readSections);
  const [allGroups, setAllGroups] = useState(false);
  const toggleSection = (id: string) =>
    setSections((s) => {
      const next = { ...s, [id]: !(s[id] ?? true) };
      try {
        localStorage.setItem(SECTION_KEY, JSON.stringify(next));
      } catch {
        // sem armazenamento: vale nesta sessão
      }
      return next;
    });
  const isOpen = (id: string) => sections[id] ?? true;
  const pinnedProjects = pins.flatMap((id) => projects?.filter((p) => p.id === id) ?? []);
  const pinnedGroups = pins.flatMap((id) =>
    id.startsWith("g:") ? (groups?.filter((g) => g.id === id.slice(2)) ?? []) : [],
  );
  const online = (home?.live ?? []).filter((l) => !l.isMe);
  const groupUnread = new Map<string, number>();
  for (const c of conversations?.conversations ?? [])
    if (c.groupId && c.unread && !c.muted)
      groupUnread.set(c.groupId, (groupUnread.get(c.groupId) ?? 0) + c.unread);
  // Quem está online em cada grupo: pessoas ao vivo nos projetos do grupo.
  const groupOnline = new Map<string, Set<string>>();
  for (const l of online) {
    const groupId = projects?.find((p) => p.id === l.projectId)?.groupId;
    if (!groupId) continue;
    const set = groupOnline.get(groupId) ?? new Set<string>();
    set.add(l.user.id);
    groupOnline.set(groupId, set);
  }
  const item = (active: boolean) =>
    cn(
      "group/item flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13.5px] transition-colors",
      active ? "bg-bg-emphasis font-medium text-fg" : "text-fg-muted hover:bg-bg-muted hover:text-fg",
    );
  const pinButton = (id: string, pinned: boolean, label: string) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        togglePin(id);
      }}
      aria-label={pinned ? `Desafixar ${label}` : `Fixar ${label}`}
      title={pinned ? "Desafixar" : "Fixar"}
      className="ml-auto hidden h-5 w-5 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg group-hover/item:flex"
    >
      {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
    </button>
  );
  const groupRow = (g: NonNullable<typeof groups>[number], pinnedRow = false) => {
    const unread = groupUnread.get(g.id) ?? 0;
    const people = groupOnline.get(g.id)?.size ?? 0;
    return (
      <Link
        key={`${pinnedRow ? "pin" : "g"}:${g.id}`}
        to={`/g/${g.slug}`}
        className={item(pathname === `/g/${g.slug}`)}
        title={`${g.name} · ${people ? `${people} online agora` : "ninguém online agora"}${g.members ? ` · ${g.members} membros` : ""}`}
      >
        <GroupAvatar group={g} unread={unread} />
        <span className={cn("truncate", unread && "font-medium text-fg")}>{g.name}</span>
        {people ? (
          <span className="flex shrink-0 items-center gap-1 text-[11px] text-accent group-hover/item:hidden">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            {people}
          </span>
        ) : null}
        {unread ? (
          <span className="ml-auto text-[11px] font-semibold text-accent group-hover/item:hidden">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
        {pinButton(`g:${g.id}`, pins.includes(`g:${g.id}`), g.name)}
      </Link>
    );
  };
  const groupList = groups ?? [];
  const shownGroups = allGroups ? groupList : groupList.slice(0, 5);
  const wide = pathname.startsWith("/projects");
  return (
    <div className="flex h-full">
      <nav
        aria-label="Navegação"
        className="flex w-[248px] shrink-0 flex-col gap-5 overflow-y-auto border-r border-border bg-bg-subtle px-3 py-3"
      >
        <div className="flex flex-col gap-0.5">
          {NAV.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={item(isActivePath(pathname, to))}
              aria-current={isActivePath(pathname, to) ? "page" : undefined}
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate">{label}</span>
              {/* Não lidas em Mensagens: sempre à vista. */}
              {to === "/chat" ? <CountPill n={unreadChat} /> : null}
            </Link>
          ))}
          {(me?.user.isAdmin || me?.user.isSuperAdmin) && !desktop ? (
            <Link to="/admin" className={item(isActivePath(pathname, "/admin"))}>
              <Shield className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span>Administração</span>
            </Link>
          ) : null}
        </div>
        {account ? <PendingInvites /> : null}
        {pinnedProjects.length || pinnedGroups.length ? (
          <div className="flex flex-col gap-0.5">
            <SidebarTitle open={isOpen("pins")} onToggle={() => toggleSection("pins")}>
              Fixados
            </SidebarTitle>
            {isOpen("pins") ? (
              <>
                {pinnedProjects.map((p) => (
                  <Link
                    key={p.id}
                    to={`/p/${p.id}`}
                    className={item(pathname === `/p/${p.id}`)}
                    title={`${p.ownerHandle}/${p.slug}`}
                  >
                    <FolderGit2 className="h-4 w-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
                    <span className="truncate">{p.name}</span>
                    {p.activeNow > 0 ? (
                      <span
                        className="h-2 w-2 shrink-0 rounded-full bg-accent group-hover/item:hidden"
                        title="Alguém programando agora"
                      />
                    ) : null}
                    {pinButton(p.id, true, p.name)}
                  </Link>
                ))}
                {pinnedGroups.map((g) => groupRow(g, true))}
              </>
            ) : null}
          </div>
        ) : null}
        {online.length ? (
          <div className="flex flex-col gap-0.5">
            <SidebarTitle open={isOpen("online")} onToggle={() => toggleSection("online")}>
              Online agora
            </SidebarTitle>
            {isOpen("online")
              ? online.slice(0, 8).map((l) => (
                  <Link
                    key={`${l.projectId}:${l.user.id}`}
                    to={`/p/${l.projectId}`}
                    className={item(false)}
                    title={`${l.user.name} está em ${l.projectName} — entrar na sessão`}
                  >
                    <span className="relative shrink-0">
                      <Avatar name={l.user.name} src={l.user.avatarUrl} color={l.user.color} size={20} />
                      <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-accent ring-2 ring-bg-subtle" />
                    </span>
                    <span className="min-w-0 truncate">
                      {l.user.name.split(" ")[0]}
                      <span className="text-fg-subtle"> · {l.projectName}</span>
                    </span>
                    <span className="ml-auto hidden text-[11px] font-medium text-accent group-hover/item:inline">
                      Entrar
                    </span>
                  </Link>
                ))
              : null}
          </div>
        ) : null}
        {FULL_EDITION && account ? (
          <div className="flex flex-col gap-0.5">
            <SidebarTitle
              to="/groups"
              open={isOpen("groups")}
              onToggle={() => toggleSection("groups")}
              action={
                <Tip label="Ver todos os grupos">
                  <Link
                    to="/groups"
                    className="rounded p-0.5 text-fg-subtle hover:bg-bg-muted hover:text-fg"
                    aria-label="Todos os grupos"
                  >
                    <Users className="h-3.5 w-3.5" />
                  </Link>
                </Tip>
              }
            >
              Grupos
            </SidebarTitle>
            {isOpen("groups") ? (
              groupList.length ? (
                <>
                  {shownGroups.map((g) => groupRow(g))}
                  {groupList.length > 5 ? (
                    <button
                      type="button"
                      onClick={() => setAllGroups((v) => !v)}
                      className="px-2.5 py-1 text-left text-[12.5px] text-fg-muted hover:text-fg"
                    >
                      {allGroups ? "Mostrar menos" : `Ver todos (${groupList.length})`}
                    </button>
                  ) : null}
                </>
              ) : (
                <Link to="/groups" className="px-2.5 text-[12.5px] text-fg-muted hover:text-fg">
                  Crie um grupo para o seu time
                </Link>
              )
            ) : null}
          </div>
        ) : null}
        <div className="mt-auto flex flex-col gap-1.5 border-t border-border pt-3">
          <SidebarFooterLinks active={(to) => isActivePath(pathname, to)} />
          <ProfileStatus />
        </div>
      </nav>
      <div className="relative min-w-0 flex-1 overflow-auto bg-bg">
        {/* Mensagens, Descobrir (o mapa da órbita) e Extensões (lista + página, como no VS Code)
            ocupam a área inteira; Projetos usa a tela larga (3 colunas e a lateral de presença); as
            outras páginas, uma coluna central. */}
        {pathname.startsWith("/local-folder/") ||
        pathname.startsWith("/chat") ||
        pathname.startsWith("/discover") ||
        pathname.startsWith("/plugins") ? (
          <main className="h-full">{children}</main>
        ) : (
          <main className={cn("mx-auto w-full px-10 py-9", wide ? "max-w-[1560px]" : "max-w-[1180px]")}>
            {children}
          </main>
        )}
      </div>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const logout = useLogout();
  // Chat funciona também sem conta (o convidado do app desktop conversa nos projetos dele).
  const account = Boolean(me);
  useChatSocket(account);
  const { data: unreadChat = 0 } = useChatUnread(account);
  useUnreadTitle(unreadChat);
  // "Me avise" da Órbita: segue valendo fora da página Descobrir (e com a janela na bandeja).
  useOrbitWatcher(Boolean(me && !me.user.isGuest));
  const { pathname } = useLocation();
  if (desktop) return <DesktopShell unreadChat={unreadChat}>{children}</DesktopShell>;
  return (
    <div className="flex min-h-full flex-col bg-bg">
      <header className="glass sticky top-0 z-40 border-b border-border">
        <div className="mx-auto flex h-14 max-w-[1120px] items-center gap-8 px-5">
          <Link to="/projects" aria-label="Início" className="shrink-0">
            <Logo />
          </Link>
          <nav className="flex h-full items-center gap-1">
            {VISIBLE_NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={cn(
                  "relative flex h-full items-center gap-1.5 px-2 text-[13px] font-medium transition-colors",
                  isActivePath(pathname, to) ? "text-fg" : "text-fg-muted hover:text-fg",
                )}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                <span className="hidden md:inline">{label}</span>
                {to === "/chat" && unreadChat > 0 ? (
                  <span
                    className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-fg px-1 text-[10px] font-semibold text-fg-inverse"
                    aria-label={`${unreadChat} mensagens não lidas`}
                  >
                    {unreadChat > 99 ? "99+" : unreadChat}
                  </span>
                ) : null}
                {isActivePath(pathname, to) ? (
                  <span className="absolute inset-x-2 -bottom-px h-px bg-fg" />
                ) : null}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1.5">
            {FULL_EDITION ? null : (
              <a
                href={DOWNLOAD_URL}
                className="mr-1 hidden h-8 items-center gap-1.5 rounded-md border border-border-strong bg-surface px-3 text-[12.5px] font-medium text-fg transition-colors hover:bg-bg-muted sm:inline-flex"
              >
                <Download className="h-3.5 w-3.5" /> Baixar o app
              </a>
            )}
            <Notifications enabled={Boolean(me)} />
            {me ? (
              <Menu>
                <MenuTrigger asChild>
                  <button
                    className="ml-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                    aria-label="Menu da conta"
                  >
                    <Avatar name={me.user.name} src={me.user.avatarUrl} size={28} />
                  </button>
                </MenuTrigger>
                <MenuContent>
                  <MenuLabel>
                    {me.user.name} · @{me.user.handle}
                  </MenuLabel>
                  {FULL_EDITION ? (
                    <>
                      <MenuItem icon={<User className="h-3.5 w-3.5" />} asChild>
                        <Link to={`/u/${me.user.handle}`}>Meu perfil</Link>
                      </MenuItem>
                      <MenuItem icon={<Settings className="h-3.5 w-3.5" />} asChild>
                        <Link to="/settings">Configurações</Link>
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem icon={<Download className="h-3.5 w-3.5" />} asChild>
                      <a href={DOWNLOAD_URL}>Baixar o Kompil Desktop</a>
                    </MenuItem>
                  )}
                  {(me.user.isAdmin || me.user.isSuperAdmin) && !desktop ? (
                    <MenuItem icon={<Shield className="h-3.5 w-3.5" />} asChild>
                      <Link to="/admin">
                        {me.user.isSuperAdmin ? "Painel da plataforma" : "Administração"}
                      </Link>
                    </MenuItem>
                  ) : null}
                  <MenuSeparator />
                  <MenuItem icon={<LogOut className="h-3.5 w-3.5" />} onSelect={() => void logout()}>
                    Sair
                  </MenuItem>
                </MenuContent>
              </Menu>
            ) : null}
          </div>
        </div>
      </header>
      <main
        className={cn(
          "mx-auto w-full flex-1 px-5 py-10",
          pathname.startsWith("/projects") ? "max-w-[1440px]" : "max-w-[1120px]",
        )}
      >
        {children}
      </main>
      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between gap-4 px-5 py-5 text-[12px] text-fg-subtle">
          <span>Código compartilhado, execução individual.</span>
          <span>Seu código é seu. Projetos nascem privados.</span>
        </div>
      </footer>
    </div>
  );
}
